import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import classNames from 'classnames';
import { Checkbox } from '@mantine/core';
import { useSendPhoneConfirmationMutation } from 'redux/Api/authApi/authApi';
import s from '../Login/Login.module.scss';
import logo from '../Login/img/logo.svg';
import { PatternFormat } from 'react-number-format';

interface LocationState {
  phone?: string;
  surname?: string;
  name?: string;
  patronymic?: string;
  email?: string;
  password?: string;
  isTermsAccepted?: boolean;
  emailVerified?: boolean;
  fromLogin?: boolean;
}

interface FormData {
  isTermsAccepted: boolean;
  isOrderNotificationsAllowed: boolean;
  isPromotionalEmailsAllowed: boolean;
}

export const RegistrationStep4: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state as LocationState;

  const [sendPhoneConfirmation] = useSendPhoneConfirmationMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<FormData>({
    defaultValues: {
      isTermsAccepted: registrationData?.isTermsAccepted || true,
      isOrderNotificationsAllowed: false,
      isPromotionalEmailsAllowed: false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!registrationData || !registrationData.emailVerified) {
      navigate('/login');
    }
  }, [registrationData, navigate]);

  const onSubmit = async (data: FormData) => {
    if (!data.isTermsAccepted) {
      setPhoneError('Необходимо принять пользовательское соглашение');
      return;
    }

    setLoading(true);
    setPhoneError('');

    try {
      await sendPhoneConfirmation({ phone: registrationData.phone! }).unwrap();

      navigate('/registration/step5', {
        state: {
          ...registrationData,
          isTermsAccepted: data.isTermsAccepted,
          isOrderNotificationsAllowed: data.isOrderNotificationsAllowed,
          isPromotionalEmailsAllowed: data.isPromotionalEmailsAllowed,
        },
      });
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Ошибка при отправке кода подтверждения на телефон';
      setPhoneError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep3 = () => {
    navigate('/registration/step3', { state: registrationData });
  };

  const formatPhoneForDisplay = (phone: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 11) {
      return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(
        4,
        7,
      )}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
    }
    return phone;
  };

  if (!registrationData) {
    return null;
  }

  return (
    <div className={s.enter}>
      <div className={s.inner}>
        <div className={s.logo}>
          <img src={logo} alt='Скилла' />
        </div>

        <h1 className={s.titleStepRegister}>Логин для входа в личный кабинет</h1>
        <div className={s.stepper}>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItem}></div>
        </div>

        <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
          <div className={s.inputGroup}>
            <label>Номер телефона</label>
            <PatternFormat
              className={classNames(s.input, s.inputDisabled)}
              format='+7 (###) ###-##-##'
              value={formatPhoneForDisplay(registrationData.phone || '')}
              disabled
            />
          </div>

          <div className={s.inputGroup}>
            <Controller
              name='isTermsAccepted'
              control={control}
              rules={{
                required: 'Необходимо принять пользовательское соглашение',
              }}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  color='#002CFB'
                  size='16px'
                  checked={value}
                  onChange={onChange}
                  label={
                    <span className={s.checkboxText}>
                      Я прочитал и принимаю{' '}
                      <a
                        href='https://skilla.ru/legal'
                        target='_blank'
                        rel='noopener noreferrer'
                        className={s.link}>
                        Пользовательское соглашение
                      </a>
                    </span>
                  }
                  styles={{
                    label: {
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#121922',
                      cursor: 'pointer',
                    },
                    inner: {
                      height: '18px',
                      display: 'flex',
                      alignItems: 'flex-start',
                    },
                  }}
                />
              )}
            />
            {errors?.isTermsAccepted?.message && (
              <div className={s.errorMessage}>{errors.isTermsAccepted.message}</div>
            )}
          </div>

          <div className={s.inputGroup}>
            <Controller
              name='isOrderNotificationsAllowed'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  color='#002CFB'
                  size='16px'
                  checked={value}
                  onChange={onChange}
                  label={
                    <span className={s.checkboxText}>
                      Соглашаюсь на получение сообщений о статусах заказов
                    </span>
                  }
                  styles={{
                    label: {
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#121922',
                      cursor: 'pointer',
                    },
                    inner: {
                      height: '18px',
                      display: 'flex',
                      alignItems: 'flex-start',
                    },
                  }}
                />
              )}
            />
          </div>

          <div className={s.inputGroup}>
            <Controller
              name='isPromotionalEmailsAllowed'
              control={control}
              render={({ field: { onChange, value } }) => (
                <Checkbox
                  color='#002CFB'
                  size='16px'
                  checked={value}
                  onChange={onChange}
                  label={
                    <span className={s.checkboxText}>
                      Соглашаюсь на рассылки рекламного характера
                    </span>
                  }
                  styles={{
                    label: {
                      fontSize: '14px',
                      lineHeight: '18px',
                      color: '#121922',
                      cursor: 'pointer',
                    },
                    inner: {
                      height: '18px',
                      display: 'flex',
                      alignItems: 'flex-start',
                    },
                  }}
                />
              )}
            />
          </div>

          {phoneError && <div className={s.errorMessage}>{phoneError}</div>}

          <button type='submit' disabled={loading} className={s.submitButton}>
            Получить код в СМС
          </button>

          <button
            type='button'
            className={s.registerButton}
            onClick={() => navigate('/login')}>
            Начать сначала
          </button>
        </form>

        <div className={s.footer}>
          <span>© {new Date().getFullYear()} go.skilla.ru</span>
        </div>
      </div>
    </div>
  );
};
